import React from 'react';
import {
  AdminConversion,
  AdminEmail,
  AdminPage,
  AdminTranscription,
} from '../../domain/admin';
import {
  DeletedBadge,
  EmailStatusBadge,
  EmailTypeBadge,
  TranscriptionStatusBadge,
  UnverifiedMediaBadge,
  VideoEditorBadge,
} from './AdminBadges';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  Timestamp,
  formatSeconds,
} from './AdminPrimitives';
import {formatBytes} from '../../lib/audioMetadata';
import {ChevronDownIcon} from '../Icons';

/**
 * The two per-user lists, behind one set of tabs. Conversions used to be a third tab here but now
 * lives at its own route (`/admin/users/:userId/conversions`, `adminUserConversions.container.tsx`)
 * — the same reason the global transcription queue is its own page rather than a tab: a list worth
 * bookmarking or refreshing directly needs its own URL. `AdminRecentConversions` still shows the
 * newest few inline, right above this panel, as a preview.
 *
 * The transcription list is the one with a rule attached: **soft-deleted rows stay in it**, badged
 * rather than filtered, because a deleted transcription still spent its seconds and "where did my
 * allowance go" is the question this tab exists to answer. Same reasoning for `rejected` — the
 * transcript was withheld, the seconds were charged.
 */

export type AdminActivityTab = 'transcriptions' | 'emails';

interface AdminUserActivityProps {
  tab: AdminActivityTab;
  onTabChange: (tab: AdminActivityTab) => void;
  offset: number;
  onOffsetChange: (offset: number) => void;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  transcriptions: AdminPage<AdminTranscription> | null;
  emails: AdminPage<AdminEmail> | null;
}

const TABS: Array<{key: AdminActivityTab; label: string}> = [
  {key: 'transcriptions', label: 'Transcriptions'},
  {key: 'emails', label: 'Email'},
];

const headerCellClass = 'px-4 py-2 font-medium';
const cellClass = 'px-4 py-2.5 align-top';

const AdminUserActivity: React.FC<AdminUserActivityProps> = ({
  tab,
  onTabChange,
  offset,
  onOffsetChange,
  pageSize,
  isLoading,
  error,
  onRetry,
  transcriptions,
  emails,
}) => {
  const page = tab === 'transcriptions' ? transcriptions : emails;
  const isEmpty = !page || page.items.length === 0;

  return (
    <Panel
      padded={false}
      title={
        <div className="flex items-center gap-1">
          {TABS.map(entry => (
            <button
              key={entry.key}
              type="button"
              onClick={() => onTabChange(entry.key)}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
                tab === entry.key
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>
      }
    >
      {error && isEmpty && <ErrorState message={error} onRetry={onRetry} />}
      {!error && isLoading && isEmpty && <LoadingState />}

      {!error && !isLoading && isEmpty && (
        <EmptyState
          message={tab === 'transcriptions' ? 'No transcriptions.' : 'No email logged for this account.'}
          hint={tab === 'emails' ? 'Accounts predating email logging show nothing here.' : undefined}
        />
      )}

      {!isEmpty && (
        <div className="overflow-x-auto">
          {tab === 'transcriptions' && <TranscriptionTable rows={transcriptions?.items ?? []} />}
          {tab === 'emails' && <EmailTable rows={emails?.items ?? []} />}
        </div>
      )}

      {page && page.total > 0 && (
        <Pagination
          total={page.total}
          limit={page.limit || pageSize}
          offset={offset}
          onOffsetChange={onOffsetChange}
          noun={tab === 'emails' ? 'emails' : tab}
          isLoading={isLoading}
        />
      )}
    </Panel>
  );
};

const TranscriptionTable: React.FC<{rows: AdminTranscription[]}> = ({rows}) => (
  <table className="w-full min-w-[820px] text-left text-xs">
    <thead>
      <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
        <th scope="col" className={headerCellClass}>Name</th>
        <th scope="col" className={headerCellClass}>Status</th>
        <th scope="col" className={`${headerCellClass} text-right`}>Duration</th>
        <th scope="col" className={`${headerCellClass} text-right`}>Size</th>
        <th scope="col" className={headerCellClass}>Created</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-800/60">
      {rows.map(row => (
        <tr key={row.id} className={row.isDeleted ? 'opacity-70' : undefined}>
          <td className={`${cellClass} max-w-[320px]`}>
            <span
              className={`block truncate text-gray-100 ${row.isDeleted ? 'line-through' : ''}`}
              title={row.name}
            >
              {row.name}
            </span>
            {row.audioFilename && (
              <span className="block truncate text-[11px] text-gray-500" title={row.audioFilename}>
                {row.audioFilename}
              </span>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {row.isDeleted && <DeletedBadge />}
              {row.assemblyaiTranscriptId && (
                <span
                  className="font-mono text-[10px] text-gray-600"
                  title="AssemblyAI transcript ID"
                >
                  {row.assemblyaiTranscriptId}
                </span>
              )}
            </div>
            {row.error && <p className="mt-1 text-[11px] text-red-300">{row.error}</p>}
          </td>
          <td className={cellClass}>
            <TranscriptionStatusBadge status={row.status} />
          </td>
          <td className={`${cellClass} text-right tabular-nums text-gray-300`}>
            {formatSeconds(row.durationSeconds)}
          </td>
          <td className={`${cellClass} text-right tabular-nums text-gray-400`}>
            {row.size ? formatBytes(row.size) : '—'}
          </td>
          <td className={`${cellClass} text-gray-400`}>
            <Timestamp iso={row.createdAt} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * Exported for reuse by `AdminRecentConversions`, the un-paginated echo of this same table on the
 * user detail page — one implementation of the row (and its expando) rather than two that could
 * drift apart.
 *
 * The four fields added 2026-08-29 (media reference, editor snapshot, fps, beep sound) are
 * support-diagnosis detail, not something worth a scan-column of its own — they live behind a
 * per-row expando instead. `audioPath` is the one exception that surfaces without expanding: its
 * absence is what hangs DaVinci Resolve on import, so a missing one gets a warning badge inline.
 */
export const ConversionTable: React.FC<{rows: AdminConversion[]}> = ({rows}) => {
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <table className="w-full min-w-[900px] text-left text-xs">
      <thead>
        <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
          <th scope="col" className={`${headerCellClass} w-8`} />
          <th scope="col" className={headerCellClass}>Created</th>
          <th scope="col" className={headerCellClass}>Mode</th>
          <th scope="col" className={headerCellClass}>Markers</th>
          <th scope="col" className={headerCellClass}>Word list</th>
          <th scope="col" className={`${headerCellClass} text-right`}>Words</th>
          <th scope="col" className={`${headerCellClass} text-right`}>Cuts</th>
          <th scope="col" className={headerCellClass}>Transcription</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-800/60">
        {rows.map((row) => {
          const isOpen = expandedIds.has(row.id);

          return (
            <React.Fragment key={row.id}>
              <tr>
                <td className={cellClass}>
                  <button
                    type="button"
                    onClick={() => toggle(row.id)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Hide conversion detail' : 'Show conversion detail'}
                    className="text-gray-500 transition-colors hover:text-gray-200 cursor-pointer"
                  >
                    <ChevronDownIcon
                      className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </td>
                <td className={`${cellClass} text-gray-400`}>
                  <Timestamp iso={row.createdAt} />
                </td>
                <td className={`${cellClass} text-gray-200`}>{row.mode}</td>
                <td className={`${cellClass} text-gray-400`}>{row.markers || 'none'}</td>
                <td className={`${cellClass} max-w-[200px] truncate text-gray-300`} title={row.wordListName ?? ''}>
                  {row.wordListName || '—'}
                </td>
                <td className={`${cellClass} text-right tabular-nums text-gray-300`}>
                  {row.censoredWords ?? '—'}
                </td>
                <td className={`${cellClass} text-right tabular-nums text-gray-300`}>
                  {row.totalCuts ?? '—'}
                </td>
                <td className={`${cellClass} font-mono text-[10px] text-gray-600`}>
                  {row.transcriptionId || '—'}
                </td>
              </tr>
              {isOpen && (
                <tr className="bg-gray-950/40">
                  <td className={cellClass} />
                  <td colSpan={7} className={`${cellClass} pb-3`}>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-gray-600">Video editor</dt>
                        <dd className="mt-1">
                          <VideoEditorBadge videoEditor={row.videoEditor} />
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-gray-600">FPS</dt>
                        <dd className="mt-1 tabular-nums text-gray-300">{row.fps ?? '—'}</dd>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-600">Beep sound</dt>
                        <dd className="mt-1 truncate text-gray-300" title={row.beepSoundPath ?? ''}>
                          {row.mode === 'beep' ? (
                            row.beepSoundPath || <span className="text-amber-300">Missing</span>
                          ) : (
                            <span className="text-gray-600">Not applicable</span>
                          )}
                        </dd>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-600">Media reference</dt>
                        <dd className="mt-1">
                          {row.audioPath ? (
                            <span className="block truncate text-gray-300" title={row.audioPath}>
                              {row.audioPath}
                            </span>
                          ) : (
                            <UnverifiedMediaBadge videoEditor={row.videoEditor} />
                          )}
                        </dd>
                      </div>
                    </dl>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const EmailTable: React.FC<{rows: AdminEmail[]}> = ({rows}) => (
  <table className="w-full min-w-[760px] text-left text-xs">
    <thead>
      <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
        <th scope="col" className={headerCellClass}>Sent</th>
        <th scope="col" className={headerCellClass}>Type</th>
        <th scope="col" className={headerCellClass}>Status</th>
        <th scope="col" className={headerCellClass}>Recipient</th>
        <th scope="col" className={headerCellClass}>Triggered by</th>
        <th scope="col" className={headerCellClass}>Provider event</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-800/60">
      {rows.map(row => (
        <tr key={row.id}>
          <td className={`${cellClass} text-gray-400`}>
            <Timestamp iso={row.createdAt} />
          </td>
          <td className={cellClass}>
            <EmailTypeBadge emailType={row.emailType} />
          </td>
          <td className={cellClass}>
            <EmailStatusBadge status={row.status} />
            {row.error && <p className="mt-1 text-[11px] text-red-300">{row.error}</p>}
          </td>
          <td className={`${cellClass} max-w-[220px] truncate text-gray-300`} title={row.recipient}>
            {row.recipient}
          </td>
          <td className={`${cellClass} text-gray-400`}>{row.triggeredBy}</td>
          <td className={`${cellClass} text-gray-500`}>
            {/*
              Blank until the Resend webhook is wired up in this environment — a pending ops step,
              not a fault. Until then every row legitimately stops at "accepted by provider".
            */}
            <Timestamp iso={row.providerEventAt} fallback="Awaiting webhook" />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default AdminUserActivity;
